import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import './carousel.scss';

const SWIPE_DIRECTIONS = {
  RIGHT: 'RIGHT',
  LEFT: 'LEFT'
};
const MIN_DISTANCE = 50;

const Carousel = ({ children, className, ...props }) => {
  const [startPosition, setStartPosition] = useState(0);
  const [currentVisibleIndex, setCurrentVisibleIndex] = useState(0);
  const [isSwipingLeft, setIsSwipingLeft] = useState(false);
  const [isSwipingRight, setIsSwipingRight] = useState(false);

  const swipeDirection = endPosition => {
    if (startPosition < endPosition) {
      return SWIPE_DIRECTIONS.RIGHT;
    }
    if (startPosition > endPosition) {
      return SWIPE_DIRECTIONS.LEFT;
    }
    return '';
  };

  const hasSwipedRight = endTouch => {
    const distance = Math.abs(endTouch.clientX - startPosition);
    return (
      distance > MIN_DISTANCE &&
      swipeDirection(endTouch.clientX) === SWIPE_DIRECTIONS.RIGHT
    );
  };

  const hasSwipedLeft = endTouch => {
    const distance = Math.abs(endTouch.clientX - startPosition);
    return (
      distance > MIN_DISTANCE &&
      swipeDirection(endTouch.clientX) === SWIPE_DIRECTIONS.LEFT
    );
  };

  const touchEnded = ({ changedTouches: [endTouch] }) => {
    if (hasSwipedRight(endTouch)) {
      if (currentVisibleIndex > 0) {
        setIsSwipingRight(true);
      }
    } else if (hasSwipedLeft(endTouch)) {
      if (currentVisibleIndex < children.length - 1) {
        setIsSwipingLeft(true);
      }
    }
  };

  const swipingLeftEnded = () => {
    setCurrentVisibleIndex(currentVisibleIndex + 1);
    setIsSwipingLeft(false);
  };
  const swipingRightEnded = () => {
    setCurrentVisibleIndex(currentVisibleIndex - 1);
    setIsSwipingRight(false);
  };

  const handleAnimationEnded = () => {
    if (isSwipingLeft) {
      swipingLeftEnded();
    }
    if (isSwipingRight) {
      swipingRightEnded();
    }
  };

  return (
    <div
      data-testid="Carousel"
      onTouchStart={ev => setStartPosition(ev.touches[0].clientX)}
      onTouchEnd={touchEnded}
      className={`sm-carousel ${className}`}
      {...props}
    >
      {children.map((child, index) => {
        const isCurrent = index === currentVisibleIndex;
        const isNext = index === currentVisibleIndex + 1;
        const isNextInCorner = index === currentVisibleIndex + 2;
        const isPrevious = index === currentVisibleIndex - 1;
        const isPreviousInCorner = index === currentVisibleIndex - 2;
        return (
          <div
            key={child.key}
            onAnimationEnd={handleAnimationEnded}
            className={classnames('sm-carousel__item-wrapper', {
              'sm-carousel__item-wrapper--current': isCurrent,
              'sm-carousel__item-wrapper--next': isNext,
              'sm-carousel__item-wrapper--previous': isPrevious,
              'sm-carousel__item-wrapper--slide-out-left':
                isSwipingLeft && (isCurrent || isPrevious),
              'sm-carousel__item-wrapper--slide-out-right':
                isSwipingRight && (isCurrent || isNext),
              'sm-carousel__item-wrapper--slide-in-left':
                isSwipingRight && isPrevious,
              'sm-carousel__item-wrapper--slide-in-right':
                isSwipingLeft && isNext,
              'sm-carousel__item-wrapper--slide-in-right-corner':
                isSwipingLeft && isNextInCorner,
              'sm-carousel__item-wrapper--slide-in-left-corner':
                isSwipingRight && isPreviousInCorner
            })}
            data-current-visible={currentVisibleIndex}
            data-index={index}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
};

Carousel.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};

Carousel.defaultProps = {
  className: null
};

export default Carousel;
